import {useEffect, useState} from "react";
import {useMutation} from "@apollo/client";
import {toast} from "react-toastify";
import {resolveErrorMessage} from "../utils/resolveErrorMessage";
import {useTranslation} from "react-i18next";
import {UpdateInstalledBaseNodeFile} from "../graphql/mutations/updateInstalledBaseNodeFile";
import {RemoveFileFromInstalledBaseNode} from "../graphql/mutations/removeFileFromInstalledBaseNode";
import {downloadFile} from "../utils/downloadFile";
import {getFileDisplayName, isImageFile} from "../utils/fileType";
import {FileListItem} from "../components/FileList";
import {NodeImageTarget, useNodeImage} from "./useNodeImage";

/**
 * Shared edit/download/delete state and handlers for a single file entry,
 * used by both the list ({@link FileListEntry}) and grid ({@link FileGridEntry})
 * renderers so they stay pure presentation. Deleting is two-step:
 * `requestDeleteFile` opens the confirmation, `handleDeleteFile` runs the mutation.
 *
 * With a `nodeImageTarget`, image files of that node additionally offer
 * "use as image" / "remove as image" (the node image role, see useNodeImage).
 */
export const useFileEntryActions = (file: FileListItem, nodeImageTarget?: NodeImageTarget) => {
    const {t} = useTranslation();
    const [editing, setEditing] = useState(false);
    const [filename, setFilename] = useState(file.filename);
    const [comment, setComment] = useState(file.comment ?? '');
    const {setNodeImage, busy: nodeImageBusy} = useNodeImage();

    const canSetAsNodeImage = !!nodeImageTarget?.canWrite
        && file.kind === 'installedBaseNode'
        && isImageFile(file.contentType)
        && !!file.fileId;
    // `currentImageId` is the node-file id, i.e. this entry's `id`.
    const isCurrentNodeImage = !!nodeImageTarget && nodeImageTarget.currentImageId === file.id;
    // "Use as asset image" / "Use as system image" / … when the type is known.
    const nodeType = nodeImageTarget?.nodeTypeLabel;
    const useAsNodeImageLabel = nodeType ? t('Use as {{nodeType}} image', {nodeType}) : t('Use as image');
    const removeAsNodeImageLabel = nodeType ? t('Remove as {{nodeType}} image', {nodeType}) : t('Remove as image');
    const nodeImageActionLabel = isCurrentNodeImage ? removeAsNodeImageLabel : useAsNodeImageLabel;

    const handleToggleNodeImage = () => {
        if (!nodeImageTarget || !canSetAsNodeImage) return;
        return setNodeImage(nodeImageTarget, isCurrentNodeImage ? null : (file.fileId as string));
    };

    // Keep local state in sync with the server value (e.g. after a refetch
    // returns a name the backend did not persist), but never while the user
    // is editing the row.
    useEffect(() => {
        if (!editing) {
            setFilename(file.filename);
            setComment(file.comment ?? '');
        }
    }, [editing, file.filename, file.comment]);

    const [updateFileMutation] = useMutation(UpdateInstalledBaseNodeFile);
    const [removeFileMutation, {loading: deleting}] = useMutation(RemoveFileFromInstalledBaseNode);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const fileDisplayName = getFileDisplayName(file);
    const showComment = file.supportsComment && !!file.comment;

    const handleSaveEdit = () => {
        const input: { filename: string; comment?: string } = {filename};
        if (file.supportsComment) {
            input.comment = comment;
        }
        updateFileMutation({
            variables: {
                installedBaseNodeFileId: file.id,
                input,
            },
            onError: (error) => {
                toast.error(resolveErrorMessage(error, t));
            },
            refetchQueries: ["AssetFiles", "SystemFiles", "InstalledBaseNodeFiles"],
        })
            .then((res) => {
                const updatedFile = res.data?.updateInstalledBaseNodeFile;
                if (updatedFile) {
                    toast.success(t('Updated file ') + updatedFile.filename);
                }
            })
            .finally(() => setEditing(false));
    };

    const handleDownload = () => {
        downloadFile(file.link, fileDisplayName);
    };

    const handleDeleteFile = () => {
        removeFileMutation({
            variables: {
                installedBaseNodeFileId: file.id,
            },
            onError: (error) => {
                toast.error(resolveErrorMessage(error, t));
            },
            refetchQueries: ["AssetFiles", "SystemFiles", "InstalledBaseNodeFiles"],
        })
            .then((res) => {
                const message = res.data?.removeFileFromInstalledBaseNode?.message;
                if (message) {
                    toast.success(message);
                }
            })
            .finally(() => setDeleteConfirmOpen(false));
    };

    return {
        editing,
        setEditing,
        filename,
        setFilename,
        comment,
        setComment,
        handleSaveEdit,
        handleDownload,
        deleteConfirmOpen,
        requestDeleteFile: () => setDeleteConfirmOpen(true),
        cancelDeleteFile: () => setDeleteConfirmOpen(false),
        handleDeleteFile,
        deleting,
        fileDisplayName,
        showComment,
        canSetAsNodeImage,
        isCurrentNodeImage,
        handleToggleNodeImage,
        nodeImageActionLabel,
        nodeImageBusy,
    };
};
